import { Link } from "react-router-dom";
import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import ExportExcel from "../../components/ExportExcel";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";

function DriverDetails() {
  const [driverDetails, setDriverDetails] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;

  useEffect(() => {
    axios
      .get(`${server}/getAllDriversMongoDb`, {
        headers: { Authorization: `${localStorage.getItem("token")}` },
      })
      .then((response) => {
        if (response.data && response.data["body-json"]?.body?.drivers) {
          setDriverDetails(response.data["body-json"].body.drivers);
        } else {
          console.error("Invalid data format from API", response.data);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("API Error:", error);
        setLoading(false);
      });
  }, []);

  // Add inside component (already has MySwal)
  const handleLeftDriver = async (driver) => {
    const driverId = driver.driverId;
    const driverName = driver.driverName;

    const result = await MySwal.fire({
      title: "Mark as Left?",
      text: `Are you sure you want to mark "${driverName}" as left?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Mark as Left!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/LeftDrivers",
          { driverId, action: "remove" },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (response.status === 200) {
          toast.success(`${driverName} marked as left`, {
            position: "top-right",
          });
          setDriverDetails((prev) =>
            prev.filter((d) => d.driverId !== driverId),
          );
        } else {
          toast.error("Action failed. Please try again.", {
            position: "top-right",
          });
        }
      } catch (error) {
        toast.error("Error occurred. Please try again.", {
          position: "top-right",
        });
      }
    }
  };

  // Filter
  const filteredRecords = driverDetails.filter((driver) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(driver).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // Calculate pagination
  // Sort by bus number (numerically, not lexically)
  filteredRecords.sort((a, b) => {
    const numA = a.busNumber?.match(/\d+/)?.[0] ?? 0;
    const numB = b.busNumber?.match(/\d+/)?.[0] ?? 0;
    return Number(numA) - Number(numB);
  });

  // Then slice for pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const columnMapping = {
    driverName: "Driver Name",
    driverDOB: "Driver DOB",
    driverPhoneNumber: "Driver Phone Number",
    driverAddress: "Driver Address",
    driverLicenseNumber: "Driver License Number",
    driverAadhaarNumber: "Driver Aadhaar Number",
    khallasiName: "Khallasi Name",
    khallasiPhoneNumber: "Khallasi Phone Number",
    licenseCategory: "License Category",
    licenseValidUpto: "License Valid Upto",
  };

  const handleExportExcel = () => {
    ExportExcel(driverDetails, "driverDetails", columnMapping);
  };

  const handleDelete = async (driverIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete driver "${driverIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_driver_details_mongodb`,
          { driverId: driverIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Driver Deleted", { position: "top-right" });
          setDriverDetails((prev) =>
            prev.filter((driver) => driver.driverId !== driverIdToDelete),
          );
        } else {
          toast.error("Please Try Again");
        }
      } catch (error) {
        toast.error("Deletion failed. Try Again");
      }
    }
  };

  const handleExportAllDriversExcel = () => {
    if (!driverDetails || driverDetails.length === 0) {
      toast.error("No driver details to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const driverRows = driverDetails.map((driver) => ({
      "Driver ID": driver.driverId || "",
      "Driver Name": driver.driverName || "",
      "Driver Phone No": driver.driverPhoneNumber || "",
      "Helper Name": driver.helperName || "",
      "Helper Phone No": driver.helperPhoneNumber || "",
      "Driver Address": driver.driverAddress || "",
      "Driver Aadhaar No": driver.driverAadhaarNumber || "",
      "Driver License No": driver.driverLicenseNumber || "",
      "Driver DOB": formatDate(driver.driverDOB),
      "License Category": driver.licenseCategory || "",
      "License Valid Upto": formatDate(driver.licenseValidUpto),
      "Bus Number": driver.busNumber || "",
      "Vehicle Number": driver.vehicleNumber || "",
    }));

    driverRows.sort((a, b) => {
      const numA = parseInt(a["Bus Number"]?.match(/\d+/)?.[0]) || 0;
      const numB = parseInt(b["Bus Number"]?.match(/\d+/)?.[0]) || 0;
      return numA - numB;
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(driverRows);

    XLSX.utils.book_append_sheet(workbook, worksheet, "Driver Details");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllDrivers_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportAllHelpersExcel = () => {
    if (!driverDetails || driverDetails.length === 0) {
      toast.error("No helper details to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const driverRows = driverDetails.map((driver) => ({
      "Helper Name": driver.helperName || "",
      "Helper Phone No": driver.helperPhoneNumber || "",
      "Bus Number": driver.busNumber || "",
      "Vehicle Number": driver.vehicleNumber || "",
    }));

    driverRows.sort((a, b) => {
      const numA = parseInt(a["Bus Number"]?.match(/\d+/)?.[0]) || 0;
      const numB = parseInt(b["Bus Number"]?.match(/\d+/)?.[0]) || 0;
      return numA - numB;
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(driverRows);

    XLSX.utils.book_append_sheet(workbook, worksheet, "Helper Details");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllHelpers_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportLicenseCatergoryWiseExcel = () => {
    if (!driverDetails || driverDetails.length === 0) {
      toast.error("No driver details to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const categoryMap = {};

    driverDetails.forEach((driver) => {
      const licenseCategory = driver.licenseCategory || "Unknown";

      const row = {
        "Driver ID": driver.driverId || "",
        "Driver Name": driver.driverName || "",
        "Driver Phone No": driver.driverPhoneNumber || "",
        "Helper Name": driver.helperName || "",
        "Helper Phone No": driver.helperPhoneNumber || "",
        "Driver Address": driver.driverAddress || "",
        "Driver Aadhaar No": driver.driverAadhaarNumber || "",
        "Driver License No": driver.driverLicenseNumber || "",
        "Driver DOB": formatDate(driver.driverDOB),
        "License Category": licenseCategory,
        "License Valid Upto": formatDate(driver.licenseValidUpto),
        "Bus Number": driver.busNumber || "",
        "Vehicle Number": driver.vehicleNumber || "",
      };

      if (!categoryMap[licenseCategory]) {
        categoryMap[licenseCategory] = [];
      }
      categoryMap[licenseCategory].push(row);
    });

    const sortByBusNumber = (arr) =>
      arr.sort((a, b) => {
        const numA = parseInt(a["Bus Number"]?.match(/\d+/)?.[0]) || 0;
        const numB = parseInt(b["Bus Number"]?.match(/\d+/)?.[0]) || 0;
        return numA - numB;
      });

    const workbook = XLSX.utils.book_new();

    Object.entries(categoryMap).forEach(([category, rows]) => {
      sortByBusNumber(rows);
      const sheet = XLSX.utils.json_to_sheet(rows);
      const sheetName = category.slice(0, 31);
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Drivers_LicenseCategoryWise_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportBusNoWiseExcel = () => {
    if (!driverDetails || driverDetails.length === 0) {
      toast.error("No driver details to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const busMap = {};

    driverDetails.forEach((driver) => {
      const busNumber = driver.busNumber || "Unknown";

      const row = {
        "Driver ID": driver.driverId || "",
        "Driver Name": driver.driverName || "",
        "Driver Phone No": driver.driverPhoneNumber || "",
        "Helper Name": driver.helperName || "",
        "Helper Phone No": driver.helperPhoneNumber || "",
        "Driver Address": driver.driverAddress || "",
        "Driver Aadhaar No": driver.driverAadhaarNumber || "",
        "Driver License No": driver.driverLicenseNumber || "",
        "Driver DOB": formatDate(driver.driverDOB),
        "License Category": driver.licenseCategory || "",
        "License Valid Upto": formatDate(driver.licenseValidUpto),
        "Bus Number": busNumber,
        "Vehicle Number": driver.vehicleNumber || "",
      };

      if (!busMap[busNumber]) {
        busMap[busNumber] = [];
      }
      busMap[busNumber].push(row);
    });

    const sortByDriverName = (arr) =>
      arr.sort((a, b) => a["Driver Name"].localeCompare(b["Driver Name"]));

    const workbook = XLSX.utils.book_new();

    Object.entries(busMap).forEach(([busNumber, rows]) => {
      sortByDriverName(rows);
      const sheet = XLSX.utils.json_to_sheet(rows);
      const safeSheetName = busNumber.slice(0, 31);
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Drivers_BusNoWise_${today}.xlsx`);

    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((driver, index) => (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Driver ID</span> &mdash;{" "}
                              {driver.driverId}
                              <br />
                              <span class="text-dark">
                                Driver Name
                              </span> &mdash; {driver.driverName}
                              <br />
                              <span class="text-dark">
                                Driver Phone No
                              </span>{" "}
                              &mdash; {driver.driverPhoneNumber} <br />
                              <span class="text-dark">
                                Helper Name
                              </span> &mdash; {driver.helperName} <br />
                              <span class="text-dark">
                                Helper Phone No
                              </span>{" "}
                              &mdash; {driver.helperPhoneNumber} <br />
                              <span class="text-dark">Driver Address</span>{" "}
                              &mdash;{" "}
                              {driver.driverAddress
                                .toLowerCase()
                                .split(" ")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1),
                                )
                                .reduce((acc, word, index) => {
                                  const last = acc.length - 1;
                                  if (index % 5 === 0) {
                                    acc.push([word]);
                                  } else {
                                    acc[last].push(word);
                                  }
                                  return acc;
                                }, [])
                                .map((group, i) => (
                                  <span key={i}>
                                    {group.join(" ")}
                                    <br />
                                  </span>
                                ))}
                              <br />
                              <span class="text-dark">
                                Driver Aadhaar No
                              </span>{" "}
                              &mdash; {driver.driverAadhaarNumber} <br />
                              <span class="text-dark">
                                Driver License No
                              </span>{" "}
                              &mdash; {driver.driverLicenseNumber} <br />
                              <span class="text-dark">
                                Driver DOB
                              </span> &mdash;{" "}
                              {new Date(driver.driverDOB).toLocaleDateString(
                                "en-GB",
                              )}
                              <br />
                              <span class="text-dark">
                                License Category
                              </span>{" "}
                              &mdash; {driver.licenseCategory} <br />
                              <span class="text-dark">
                                License Valid Upto
                              </span>{" "}
                              &mdash;{" "}
                              {new Date(
                                driver.licenseValidUpto,
                              ).toLocaleDateString("en-GB")}
                              <hr className="w-25" />
                              <span class="text-dark">Bus No</span> &mdash;{" "}
                              {driver.busNumber} <br />
                              <span class="text-dark">
                                Vehicle No
                              </span> &mdash; {driver.vehicleNumber} <br />
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <Link
                                  to="/update-driver"
                                  state={{
                                    driverId: driver.driverId,
                                  }}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </Link>
                                <span
                                  onClick={() => handleLeftDriver(driver)}
                                  class="badge bg-warning-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Left Driver
                                </span>
                                <span
                                  onClick={() => handleDelete(driver.driverId)}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          to="/add-driver"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Driver
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Transport Management</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Driver Details
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Driver Details
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.5 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportAllDriversExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All Driver Details
                                </span>
                                <span
                                  onClick={handleExportAllHelpersExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Helper Report
                                </span>
                                <span
                                  onClick={
                                    handleExportLicenseCatergoryWiseExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  License Category Wise
                                </span>
                                <span
                                  onClick={handleExportBusNoWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Bus No. Wise
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default DriverDetails;
